import React from 'react'
import PropTypes from 'prop-types'
import { TransitionGroup, CSSTransition } from 'react-transition-group'
import styled from '@emotion/styled'
import { ThemeProvider } from '@emotion/react'
import { PAYPAL_THEME } from '@paypalcorp/pp-react-theme'
import { isEqual } from 'lodash-es'
import StepperType from './StepperType'
import { isMobileNative } from 'utils/mobile'

const Animation = styled(TransitionGroup)`
  &.next .step-enter {
    transform: translate3d(100%, 0, 0);
  }

  &.next .step-enter-active {
    transform: translate3d(0%, 0, 0);
    transition: transform 250ms ease-in-out;
  }

  &.next .step-exit {
    transform: translate3d(0%, 0, 0);
    display: none;
  }

  &.next .step-exit-active {
    transform: translate3d(-100%, 0, 0);
    transition: transform 250ms ease-in-out;
  }

  &.back .step-enter {
    transform: translate3d(-100%, 0, 0);
  }

  &.back .step-enter-active {
    transform: translate3d(0%, 0, 0);
    transition: transform 250ms ease-in-out;
  }

  &.back .step-exit {
    transform: translate3d(0%, 0, 0);
    display: none;
  }

  &.back .step-exit-active {
    transform: translate3d(100%, 0, 0);
    transition: transform 250ms ease-in-out;
  }
`

const VALID_CHILD_TYPES = ['Step', 'StepList', 'StepSwitch', 'StepGo']

class StepList extends React.Component<any, any> {
  static displayName = 'StepList'

  childRefs: any[]
  lastStepIndex: number
  childIndexMap: Record<string, number>
  currentChild: any

  static propTypes = {
    // name attribute is used in StepSwitch
    // eslint-disable-next-line react/no-unused-prop-types
    worldReady: PropTypes.object,
    name: PropTypes.string,
    stepper: StepperType,
    children: PropTypes.node,
    navigation: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    navigationProps: PropTypes.object,
    firstStep: PropTypes.number,
    firstStepSwitchName: PropTypes.string,
    layout: PropTypes.func,
    onFirstStepBack: PropTypes.func,
    onClose: PropTypes.func,
    hideStepsBeforeFirst: PropTypes.bool,
    allowFirstStepUpdate: PropTypes.bool,
    onMount: PropTypes.func,
    flowName: PropTypes.string,
    terminalSteps: PropTypes.array,
    nonTerminalSteps: PropTypes.array,
    loadingSteps: PropTypes.array,
    onStepNavigation: PropTypes.func,
    reRenderStepOnPropsUpdate: PropTypes.bool,
    modalOverrideCss: PropTypes.object,
    navBarOverrideCss: PropTypes.object,
  }

  static defaultProps = {
    navigationProps: {},
    firstStep: 0,
  }

  constructor(props) {
    super(props)

    this.childRefs = []
    this.lastStepIndex = (this.props as any).children.length - 1
    this.childIndexMap = {}

    const children = React.Children.toArray((this.props as any).children)
    children.forEach((child: any, index) => {
      this.validateChildType(child)
      this.childRefs.push(React.createRef())
      if (child.props.name) {
        this.childIndexMap[child.props.name] = index
      }
    })

    const currentStepIndex = props.firstStep
    const currentChild =
      currentStepIndex > -1 ? (
        this.createChildAtIndex(props.firstStep, props.firstStepSwitchName, undefined)
      ) : (
        <></>
      )

    this.state = {
      currentStepIndex,
      currentChild,
      isMovingForward: true,
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if ((this.props as any).allowFirstStepUpdate) {
      const { firstStep, firstStepSwitchName, overridesPrevProps } = nextProps as any
      const shouldUpdate =
        (this.props as any).firstStep !== firstStep &&
        (this.props as any).firstStepSwitchName !== firstStepSwitchName

      if (shouldUpdate) {
        if (overridesPrevProps) {
          this.goToStepIndex(firstStep, firstStepSwitchName, overridesPrevProps)
        } else {
          this.goToStepIndex(firstStep, firstStepSwitchName, undefined)
        }
      }
    }
  }

  componentDidMount() {
    if ((this.props as any).onMount) {
      ;(this.props as any).onMount(this.stepperProps())
    }
  }

  validateChildType(child) {
    const childName = child.type.displayName
    if (!VALID_CHILD_TYPES.includes(childName)) {
      throw new Error(`Invalid StepList child type '${childName}'`)
    }
  }

  triggerOnStepNavigation = () => {
    if ((this.props as any).onStepNavigation) {
      ;(this.props as any).onStepNavigation()
    }
  }

  previousStep = (switchStepName, passedProps) => {
    // Can be assigned directly to the DOM event handler, value passed could be an object
    if (typeof switchStepName !== 'string') {
      switchStepName = null
    }

    if (typeof passedProps !== 'object') {
      passedProps = {}
    }

    // Move to the previous step in nested step list
    const { currentStepIndex } = this.state as any
    const nestedStepList = this.getNestedStepList()
    if (nestedStepList && !nestedStepList.isFirstStep()) {
      nestedStepList.previousStep(switchStepName, passedProps)
      return
    }

    // Do nothing if it is the first step
    if (this.isFirstStep()) {
      return
    }

    // Move to the preivous step of current step list
    const stepIndex = currentStepIndex - 1
    let previousChild = this.createChildAtIndex(stepIndex, switchStepName, passedProps)

    // To proceed with previous step without triggering animation,
    // used mainly to navigate to the previous step after a data fetch on
    // page load
    const hideAnimation = passedProps.hideAnimation

    this.triggerOnStepNavigation()

    this.setState({
      currentStepIndex: stepIndex,
      currentChild: previousChild,
      isMovingForward: false,
      hideAnimation,
    })
  }

  nextStep = (switchStepName, passedProps) => {
    // Can be assigned directly to the DOM event handler, value passed could be an object
    if (typeof switchStepName !== 'string') {
      switchStepName = null
    }

    if (typeof passedProps !== 'object') {
      passedProps = {}
    }

    // Move to the next step in nested step list
    const { currentStepIndex } = this.state as any
    const nestedStepList = this.getNestedStepList()
    if (nestedStepList && !nestedStepList.isLastStep()) {
      nestedStepList.nextStep(switchStepName, passedProps)
      return
    }

    // Do nothing if it is the last step
    if (this.isLastStep()) {
      return
    }

    // Move to the next step of current step list
    const stepIndex = currentStepIndex + 1
    let nextChild = this.createChildAtIndex(stepIndex, switchStepName, passedProps)

    // To proceed with previous step without triggering animation,
    // used mainly to navigate to the next step after a data fetch on
    // page load
    const hideAnimation = passedProps.hideAnimation

    this.triggerOnStepNavigation()

    this.setState({
      currentStepIndex: stepIndex,
      currentChild: nextChild,
      isMovingForward: true,
      hideAnimation,
    })
  }

  getNestedStepList() {
    let childRef = this.childRefs[(this.state as any).currentStepIndex] || {}
    let curChild = childRef.current

    // For Switch component
    if (curChild && curChild.getCurrentChildRef) {
      childRef = curChild.getCurrentChildRef()
    }
    return childRef.current instanceof StepList ? childRef.current : null
  }

  isShowNext = () => {
    if (!this.isLastStep()) {
      return true
    }

    const nestedStepList = this.getNestedStepList()
    return nestedStepList ? !nestedStepList.isLastStep() : false
  }

  isShowPrevious = () => {
    if (!this.isFirstStep()) {
      return true
    }

    const nestedStepList = this.getNestedStepList()
    return nestedStepList ? !nestedStepList.isFirstStep() : false
  }

  isLastStep = () => {
    return (this.state as any).currentStepIndex === this.lastStepIndex
  }

  isFirstStep = () => {
    return (
      (this.state as any).currentStepIndex === 0 ||
      ((this.props as any).hideStepsBeforeFirst &&
        (this.state as any).currentStepIndex === (this.props as any).firstStep)
    )
  }

  goToPreviousStep = (stepName, switchStepName, passedProps) => {
    const parentStepper = (this.props as any).stepper
    const { currentStepIndex } = this.state as any
    const childIndex = this.childIndexMap[stepName]

    if (typeof passedProps !== 'object') {
      passedProps = {}
    }

    if (childIndex > -1 && childIndex < currentStepIndex) {
      this.goToStepIndex(childIndex, switchStepName, passedProps)
    } else if (parentStepper) {
      parentStepper.goToPreviousStep(stepName, switchStepName, passedProps)
    }
  }

  goToNextStep = (stepName, switchStepName, passedProps) => {
    const parentStepper = (this.props as any).stepper
    const { currentStepIndex } = this.state as any
    const childIndex = this.childIndexMap[stepName]

    if (typeof passedProps !== 'object') {
      passedProps = {}
    }

    if (childIndex > -1 && childIndex > currentStepIndex) {
      this.goToStepIndex(childIndex, switchStepName, passedProps)
    } else if (parentStepper) {
      parentStepper.goToNextStep(stepName, switchStepName, passedProps)
    }
  }

  goToStepIndex(index, switchStepName, passedProps) {
    this.triggerOnStepNavigation()
    this.setState({
      currentStepIndex: index,
      currentChild: this.createChildAtIndex(index, switchStepName, passedProps),
    })
  }

  propsPassedToChildren() {
    // eslint-disable-next-line no-unused-vars
    const { children, navigation, stepper, firstStep, ...rest } = this.props as any
    return rest
  }

  createChildAtIndex(index, switchStepName, passedProps) {
    const child = React.Children.toArray((this.props as any).children)[index] as any
    const props = this.stepperProps({
      ref: this.childRefs[index],
      ...this.propsPassedToChildren(),
      ...passedProps,
    })
    const childType = child.type.displayName
    if (childType === 'StepSwitch') {
      props.stepper.switchStepIdentifier = {
        index,
        name: switchStepName || 'default',
      }
    }

    if (VALID_CHILD_TYPES.includes(childType)) {
      props.stepper.goToPreviousStep = this.goToPreviousStep
      props.stepper.goToNextStep = this.goToNextStep
    }
    return React.cloneElement(child as any, props)
  }

  createChildForCurrentStep(switchStepName) {
    return this.createChildAtIndex((this.state as any).currentStepIndex, switchStepName, undefined)
  }

  getCurrentStepIndex = () => {
    return (this.state as any).currentStepIndex
  }

  getCurrentStepName = () => {
    const currentChildProps = (this.state as any).currentChild.props
    if (currentChildProps?.children) {
      return currentChildProps?.stepper?.switchStepIdentifier?.name
    }
    return currentChildProps?.name
  }

  stepperProps(props?) {
    return Object.assign(
      {
        stepper: {
          name: (this.props as any).name,
          previousStep: this.previousStep,
          nextStep: this.nextStep,
          goToPreviousStep: this.goToPreviousStep,
          goToNextStep: this.goToNextStep,
          isShowNext: this.isShowNext,
          isShowPrevious: this.isShowPrevious,
          isFirstStep: this.isFirstStep,
          isLastStep: this.isLastStep,
          getCurrentStepIndex: this.getCurrentStepIndex,
          getCurrentStepName: this.getCurrentStepName,
        },
        parentStepper: (this.props as any).stepper,
      },
      props,
    )
  }

  renderNavigation() {
    const { navigation: nav, navigationProps, worldReady } = this.props as any
    const navProps = { ...navigationProps, worldReady }
    return nav ? React.createElement(nav, this.stepperProps(navProps)) : null
  }

  render() {
    const { currentStepIndex, isMovingForward, currentChild } = this.state as any
    let className = isMovingForward ? 'next' : 'back'
    if ((this.state as any).hideAnimation) {
      className = ''
    }
    const {
      layout,
      worldReady,
      onFirstStepBack,
      onClose,
      flowName,
      terminalSteps,
      nonTerminalSteps,
      loadingSteps,
      reRenderStepOnPropsUpdate,
      modalOverrideCss,
      navBarOverrideCss,
    } = this.props as any

    let stepperProps = {}
    let layoutProps = {}
    let Layout = React.Fragment

    if (layout) {
      stepperProps = this.stepperProps({ worldReady })
      layoutProps = {
        onFirstStepBack,
        onClose,
        flowName,
        terminalSteps,
        nonTerminalSteps,
        loadingSteps,
        modalOverrideCss,
        navBarOverrideCss,
      }
      Layout = layout
    }

    // currentChild was not updated when we navigated between static pages
    // We have to clone currentChild and override it with the updated props
    let children = currentChild
    if (currentChild && reRenderStepOnPropsUpdate) {
      const latestProps = this.propsPassedToChildren()
      if (!isEqual(latestProps, currentChild.props)) {
        this.currentChild = currentChild
        children = React.cloneElement(this.currentChild, latestProps)
      }
    }

    return (
      <ThemeProvider theme={PAYPAL_THEME}>
        <Layout {...stepperProps} {...layoutProps}>
          {this.renderNavigation()}
          {/* OSLO/Venice WKWebView: skip translate3d CSSTransitions — they promote
              full-step layers onto the GPU and can crash com.apple.WebKit.GPU. */}
          {isMobileNative() ? (
            <div id="animation">{children}</div>
          ) : (
            <Animation id="animation" className={className}>
              <CSSTransition key={currentStepIndex} timeout={250} classNames="step">
                {children}
              </CSSTransition>
            </Animation>
          )}
        </Layout>
      </ThemeProvider>
    )
  }
}

;(StepList as any).propTypes = {
  stepper: StepperType,
  overridesPrevProps: PropTypes.object,
}

export default StepList
